import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, Flame, BookOpen } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface LeaderboardEntry {
  user_id: string;
  total_points: number;
  current_streak_days: number;
  courses_completed: number;
  profiles?: { first_name: string; last_name: string };
}

export function Leaderboard() {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('points');

  useEffect(() => {
    loadLeaderboard();
  }, [activeTab]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const orderBy = activeTab === 'points' ? 'total_points' 
        : activeTab === 'streak' ? 'current_streak_days'
        : 'courses_completed';

      const { data: engagementData, error } = await supabase
        .from('user_engagement')
        .select('*')
        .order(orderBy, { ascending: false })
        .limit(10);

      if (error) throw error;

      // Fetch profiles separately
      const userIds = engagementData?.map(e => e.user_id) || [];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      const profileMap = new Map(profilesData?.map(p => [p.id, p]));
      const enriched = engagementData?.map(e => ({
        ...e,
        profiles: profileMap.get(e.user_id)
      })) || [];

      setLeaderboard(enriched);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (index: number) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return `${index + 1}`;
  };

  const getValue = (entry: LeaderboardEntry) => {
    if (activeTab === 'points') return entry.total_points;
    if (activeTab === 'streak') return entry.current_streak_days;
    return entry.courses_completed;
  };

  const getLabel = () => {
    if (activeTab === 'points') return 'points';
    if (activeTab === 'streak') return 'day streak';
    return 'courses';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5" />
          Leaderboard
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="points">
              <Trophy className="h-4 w-4 mr-1" />
              Points
            </TabsTrigger>
            <TabsTrigger value="streak">
              <Flame className="h-4 w-4 mr-1" />
              Streak
            </TabsTrigger>
            <TabsTrigger value="courses">
              <BookOpen className="h-4 w-4 mr-1" />
              Courses
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            <div className="space-y-2">
              {leaderboard.map((entry, index) => (
                <div
                  key={entry.user_id}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    entry.user_id === user?.id ? 'bg-primary/10 border border-primary/20' : 'bg-muted/50'
                  }`}
                >
                  <div className="text-2xl font-bold w-8 text-center">
                    {getRankIcon(index)}
                  </div>
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="text-xs">
                      {entry.profiles?.first_name?.[0]}{entry.profiles?.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {entry.profiles?.first_name} {entry.profiles?.last_name}
                      {entry.user_id === user?.id && (
                        <Badge variant="secondary" className="ml-2 text-xs">You</Badge>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline" className="font-bold">
                    {getValue(entry)} {getLabel()}
                  </Badge>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}